<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary fc-row-with-search">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl">สถานะใบเสร็จ</label>
          <b-form-select
            v-model="receiptStatus"
            :options="receiptStatusOpt"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>

        <div class="field">
          <label class="field-lbl"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            v-model.trim="searchTable"
            type="search"
            size="sm"
            class="field-input-native"
            placeholder="ชื่อคนไข้, DN, เลขที่ใบเสร็จ หรือสาขา"
          />
        </div>

        <b-button
          variant="primary"
          class="btn-primary-lg"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-multi">
        <ClinicBranchMultiSelect
          :value="branchFilter"
          :options="branchOptions"
          @input="$emit('update:branchFilter', $event)"
        />
      </div>
    </div>

    <div id="report-elements">
      <div class="d-flex flex-wrap align-items-center mb-2">
        <b-form-radio-group
          v-model="view"
          :options="viewOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          aria-label="มุมมองตาราง"
          class="mr-3"
        ></b-form-radio-group>
        <small class="text-muted">{{ viewHint }}</small>
      </div>

      <ClinicBranchSummary
        :rows="filteredList"
        :metrics="allSummaryMetrics"
        table-id="ClinicReceiptBranchSummary"
      />

      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        id="ClinicReceiptIncomeTable"
        ref="ClinicReceiptIncomeTable"
        class="my-0 sticky-footer report-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="tableFields"
        sort-by="branchName"
        :responsive="true"
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
        @row-dblclicked="openReceipt"
      >
        <template #cell(index)="row">{{ row.index + 1 }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(time)="row">{{ formatTime(row.item.creationDt) }}</template>
        <template #cell(status)="row"
          ><span
            v-if="row.item.cancelFlag"
            v-b-popover.hover.bottomleft="cancelPopoverConfig(row.item)"
          >
            ยกเลิก
          </span></template
        >
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td v-for="field in tableFields" :key="field.key">
              {{ isSumField(field.key) ? formatNumber(sum(field.key)) : "" }}
            </td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import ClinicBranchSummary from "@/components/reports/ClinicBranchSummary";
import ClinicBranchMultiSelect from "@/components/reports/ClinicBranchMultiSelect";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import { branchNameOptions, matchesBranches } from "@/utils/clinicReportFilters";
import { paymentMethodColumns, withPaymentAmounts } from "@/utils/clinicReceiptPayments";

export default {
  name: "ClinicReceiptIncomeReport",
  components: {
    Dialog,
    Loading,
    ClinicBranchSummary,
    ClinicBranchMultiSelect,
  },
  props: {
    // Selected branch names, shared with the other clinic report tabs. Empty = all.
    branchFilter: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานรายรับ แยกตามใบเสร็จ (คลินิก)",
      reportNameEn: "Clinic-Income-Receipt",

      dateRange: [],
      receiptStatus: 0,
      receiptStatusOpt: [
        { value: null, text: "ทั้งหมด" },
        { value: "0", text: "ปกติ" },
        { value: "1", text: "ยกเลิก" },
      ],

      tableBusy: false,
      receiptsList: [],
      fields: [
        { key: "index", label: "" },
        { key: "branchName", label: "สาขา", sortable: true, class: "narrow-spacing" },
        { key: "creationDt", label: "วันที่", sortable: true, class: "narrow-spacing" },
        { key: "time", label: "เวลา", class: "narrow-spacing" },
        {
          key: "receiptNo",
          label: "เลขที่ใบเสร็จ",
          class: "narrow-spacing",
          tdAttr: { "data-t": "s" },
        },
        { key: "dn", label: "DN.", class: "narrow-spacing" },
        { key: "patientFullName", label: "ชื่อคนไข้", class: "narrow-spacing" },
      ],
      moneyFields: [
        { key: "price", label: "ค่ารักษา", class: "text-right", formatter: "formatNumber" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right", formatter: "formatNumber" },
        { key: "amount", label: "ค่ารักษาสุทธิ", class: "text-right", formatter: "formatNumber" },
        { key: "paid", label: "ชำระ", class: "text-right", formatter: "formatNumber" },
        { key: "overdue", label: "ค้างชำระ", class: "text-right text-danger", formatter: "formatNumber" },
      ],
      sumKeys: ["price", "finalDiscount", "amount", "paid", "overdue"],
      // The money columns don't fit on screen with one column per payment method, so
      // the screen shows one group at a time. Exports always carry every column.
      view: "receipt",
      viewOptions: [
        { value: "receipt", text: "ยอดใบเสร็จ" },
        { value: "payment", text: "ช่องทางการชำระ" },
      ],
      isExporting: false,
      summaryMetrics: [
        { key: "price", label: "ค่ารักษา" },
        { key: "finalDiscount", label: "ส่วนลด" },
        { key: "amount", label: "ค่ารักษาสุทธิ" },
        { key: "paid", label: "ชำระ" },
        { key: "overdue", label: "ค้างชำระ" },
      ],

      searchTable: "",
      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    branchOptions() {
      return branchNameOptions(this.receiptsList);
    },
    // Branch/search-filtered receipts, still carrying their raw receiptPayment list.
    visibleReceipts() {
      return this.receiptsList
        .filter(i => matchesBranches(i.branchName, this.branchFilter))
        .filter(i =>
          matchesSearchQuery(i, this.searchTable, [
            "receiptNo",
            "patientFullName",
            "dn",
            "branchName",
          ])
        )
        .slice(0)
        .sort((a, b) => {
          const byBranch = String(a.branchName || "").localeCompare(
            String(b.branchName || ""),
            "th"
          );
          if (byBranch !== 0) return byBranch;
          return moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf();
        });
    },
    // One column per payment method (grouped by name across branches) that took money.
    paymentColumns() {
      return paymentMethodColumns(this.visibleReceipts);
    },
    filteredList() {
      return this.visibleReceipts.map(i => ({
        ...withPaymentAmounts(i, this.paymentColumns),
        _rowVariant: i.cancelFlag ? "secondary" : null,
      }));
    },
    showReceiptColumns() {
      return this.isExporting || this.view === "receipt";
    },
    showPaymentColumns() {
      return this.isExporting || this.view === "payment";
    },
    viewHint() {
      if (this.view === "receipt") return "ค่ารักษา ส่วนลด และยอดชำระ";
      return this.paymentColumns.length
        ? "แยกยอดชำระตามช่องทาง เฉพาะช่องทางที่มียอดในช่วงที่เลือก"
        : "ไม่มียอดชำระในช่วงที่เลือก";
    },
    // In the payment view ชำระ stays as the row total of the payment columns.
    visibleMoneyFields() {
      if (this.showReceiptColumns) return this.moneyFields;
      return this.moneyFields
        .filter(f => f.key === "paid")
        .map(f => ({ ...f, label: "รวมชำระ" }));
    },
    tableFields() {
      const paymentFields = this.showPaymentColumns
        ? this.paymentColumns.map(c => ({
            key: c.key,
            label: c.name,
            class: "text-right",
            thClass: "narrow-spacing",
            formatter: "formatNumber",
          }))
        : [];
      return [
        ...this.fields,
        ...(this.showReceiptColumns ? this.visibleMoneyFields : []),
        ...paymentFields,
        ...(this.showReceiptColumns ? [] : this.visibleMoneyFields),
        { key: "status", label: "" },
      ];
    },
    allSumKeys() {
      return [...this.sumKeys, ...this.paymentColumns.map(c => c.key)];
    },
    allSummaryMetrics() {
      const paymentMetrics = this.showPaymentColumns
        ? this.paymentColumns.map(c => ({ key: c.key, label: c.name }))
        : [];
      if (this.showReceiptColumns) return [...this.summaryMetrics, ...paymentMetrics];
      return [...paymentMetrics, { key: "paid", label: "รวมชำระ" }];
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").toISOString(true)
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").toISOString(true)
        : "";
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
    branchFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchClinicBranchReceipt: "moduleReport/fetchClinicBranchReceipt",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    moment() {
      return moment();
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatTime(date) {
      return date ? moment(date).locale("th").format("HH:mm") : "";
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isSumField(key) {
      return this.allSumKeys.includes(key);
    },
    sum(key) {
      if (!this.isSumField(key)) return null;
      return this.filteredList.reduce((accum, item) => {
        const data = item[key];
        if (typeof data != "number") return accum;
        return Math.round((accum + data) * 100) / 100;
      }, 0);
    },
    fetchReport() {
      if (this.dateFrom === "" || this.dateTo === "") return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchClinicBranchReceipt({
        clinicUrl: this.$route.params.clinicUrl,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        receiptStatus: this.receiptStatus,
      })
        .then(res => {
          this.receiptsList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    cancelPopoverConfig(e) {
      return {
        html: true,
        variant: "danger",
        title: () => `ใบเสร็จ ${e.receiptNo} ถูกยกเลิก`,
        content: () => `โดย:<em> ${e.cancelFirstName ?? ""} ${e.cancelLastName ?? ""}</em><br>
          วันเวลา:<em> ${this.formatDate(e.cancelDt) ?? ""} ${this.formatTime(e.cancelDt) ?? ""}</em><br>
          สาเหตุ:<em> ${e.cancelRemark ?? ""}</em>`,
      };
    },
    exportOptions() {
      return {
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets: [
          { name: "สรุปตามสาขา", tableId: "ClinicReceiptBranchSummary" },
          { name: "รายใบเสร็จ", tableId: "ClinicReceiptIncomeTable" },
        ],
      };
    },
    // Renders every row and every column (both views) for the export, then restores
    // the screen.
    async withFullTables(exportFn) {
      this.perPage = null;
      this.isExporting = true;
      try {
        await this.$nextTick();
        exportFn();
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถส่งออกรายงานได้");
      } finally {
        this.isExporting = false;
        this.perPage = 100;
      }
    },
    excelExport() {
      return this.withFullTables(() => exportTableToExcel(this.exportOptions()));
    },
    pdfExport() {
      return this.withFullTables(() =>
        exportReportToPdf({ ...this.exportOptions(), autoFit: true })
      );
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(row) {
      eventBus.$emit("openReceipt", row);
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped></style>
